import React, {Fragment, PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import color from 'color';
import {Manager, Target} from 'react-popper';
import {toast} from 'react-toastify';
import countBy from 'lodash/countBy';
import unescape from 'lodash/unescape';
import Media from 'react-media';
import TeamPoints from './TeamPoints';
import Team from './Team';
import Question from './Question';
import KickedPlayerModal from './KickedPlayerModal';
import SendGiftModal from './SendGiftModal';
import InteractiveNavigation from './InteractiveNavigation';
import {Detection} from '../../utils/detection';
import {
  Modal,
  ThemeContext,
  Button,
  Popover,
  confirm,
  Link,
  Icon,
  CoinsPackModal,
} from '../common';
import {appId, history} from '../../store';
import {
  GAMEADMINTYPE,
  GAME_PHASE_WAITING_FOR_PLAYERS,
  GAME_PHASE_PLAYING,
  GAME_PHASE_FINISHED,
  NOMEDIASOURCE,
} from '../../configs/constants';
import {
  gameLeaveMessage,
  adminPopoverMessage,
  othersPopoverMessage,
  adminPopoverTitle,
  othersPopoverTitle,
  winFinishMessage,
  tieFinishMessage,
  gameLeaveWithRemoveMessage,
  gameKickConfirmMessage,
  youKickedMessage,
} from '../../configs/messages';
import QuestionImage from './Question/QuestionImage';
import QuestionVideo from './Question/QuestionVideo';
import {forwardTo} from '../../utils';
import ChangeMediaConfigurationModal from './ChangeMediaConfigurationModal';
import silverCoinImage from '../../assets/yakcoin_silver.png';

export default class Game extends PureComponent {
  static toastProps = {position: toast.POSITION.TOP_CENTER, autoClose: 10000};

  static propTypes = {
    application: PropTypes.shape({
      ableToSendGiftsForFree: PropTypes.bool,
    }).isRequired,
    setSidebarStatus: PropTypes.func.isRequired,
    setMediaSettings: PropTypes.func.isRequired,
    kickPlayer: PropTypes.func.isRequired,
    onPlayerReady: PropTypes.func.isRequired,
    onPlayerLeave: PropTypes.func.isRequired,
    onAnswerSelected: PropTypes.func.isRequired,
    onChatToggle: PropTypes.func.isRequired,
    onPlayerAnswer: PropTypes.func.isRequired,
    onPlayerSendGift: PropTypes.func.isRequired,
    onPlayerNotesClear: PropTypes.func.isRequired,
    onPlayerKickCancel: PropTypes.func.isRequired,
    confirmMedia: PropTypes.func.isRequired,
    panel: PropTypes.shape({
      isChatPanelOpened: PropTypes.bool,
    }).isRequired,
    gameConfig: PropTypes.shape({
      hasLeaderboard: PropTypes.bool,
      hasTreasureChest: PropTypes.bool,
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        gameId: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    user: PropTypes.shape({
      mediaSettings: PropTypes.bool.isRequired,
      audioSource: PropTypes.string,
      videoSource: PropTypes.string,
      coins: PropTypes.shape({}).isRequired,
    }).isRequired,
    reInitializeVideo: PropTypes.func.isRequired,
    game: PropTypes.shape({
      currentGame: PropTypes.shape({
        players: PropTypes.arrayOf(
          PropTypes.shape({})
        ),
        teams: PropTypes.shape({}),
        state: PropTypes.string.isRequired,
        round: PropTypes.shape({
          title: PropTypes.string,
        }),
        kickRequest: PropTypes.shape({}),
      }).isRequired,
    }).isRequired,
    gifts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    updateUserCoins: PropTypes.func.isRequired,
  };

  static defaultProps = {
    players: [],
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const {questionIndex: prevQuestionIndex, isVideoPlaying: prevIsVideoPlaying} = prevState;
    const {
      game: {
        currentGame: {
          state: phase,
          questionIndex,
          responder: {id: activePlayerId} = {id: null},
        },
      },
    } = nextProps;
    const isVideoPlaying = (prevQuestionIndex !== questionIndex || phase !== GAME_PHASE_PLAYING)
      ? false
      : prevIsVideoPlaying;
    const state = {
      phase, activePlayerId, isVideoPlaying, questionIndex,
    };

    return state;
  }

  constructor(props) {
    super(props);
    const {
      game: {
        currentGame: {
          state: phase = GAME_PHASE_WAITING_FOR_PLAYERS,
        },
      },
    } = this.props;

    this.state = {
      isCoinPacksModalOpen: false,
      isSendGiftModalOpen: false,
      isReadyClicked: false,
      isVideoPlaying: false,
      questionIndex: -1,
      playerId: null,
      toastsDisplayed: false,
      phase,
      isTablet: window.innerWidth < 880,
      displayGameSettings: false,
      arePlayersExpanded: true,
    };
  }

  resizeHandler = () => {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    setTimeout(() => {
      vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }, 500);
  }

  componentDidMount() {
    this.unblock = history.block(() => gameLeaveMessage);
    window.addEventListener('beforeunload', this.onBeforeUnload);

    const vh = window.innerHeight * 0.01;
    if (window.innerWidth < 700) {
      document.documentElement.classList.add('game-in-progress');
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('overflow', 'hidden');
      window.addEventListener('resize', this.resizeHandler);
    }
  }

  componentDidUpdate() {
    const {game: {currentGame: {youKicked}}} = this.props;

    if (youKicked && !this.youKickedToastId) {
      if (this.unblock) {
        this.unblock();
      }
      this.youKickedToastId = toast.error(youKickedMessage, {
        onClose: () => forwardTo('/lobby'),
        pauseOnHover: false,
      });
    }
  }

  componentWillUnmount() {
    toast.dismiss();
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    if (window.innerWidth < 700) {
      document.documentElement.classList.remove('game-in-progress');
      document.documentElement.style.setProperty('overflow', 'scroll');
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.unblock) {
      this.unblock();
    }
  }

  onBeforeUnload = (event) => {
    toast.dismiss();
    if (Detection.isAndroid) {
      return {};
    }
    (event || window.event).returnValue = gameLeaveMessage;

    return gameLeaveMessage;
  };

  onVoteForKickClicked = (playerId) => {
    const currentPlayer = this.getCurrentPlayer();
    if (currentPlayer && playerId !== currentPlayer.id) {
      confirm({
        theme: this.theme,
        message: gameKickConfirmMessage,
        proceed: () => this.onPlayerKicked(playerId),
        okLabel: 'Yes',
        cancelLabel: 'No',
      });
    }
  };

  onSendGiftClicked = (playerId) => {
    const currentPlayer = this.getCurrentPlayer();
    const audiencePlayer = !currentPlayer;
    if (audiencePlayer || (currentPlayer && playerId !== currentPlayer.id)) {
      this.setState({isSendGiftModalOpen: true, playerId});
    }
  };

  onSelectPlayer = (playerId) => {
    this.setState({playerId});
  }

  onPlayerKicked = (playerId) => {
    const kickedPlayer = this.findUserById(playerId);
    const {kickPlayer, match: {params: {gameId}}} = this.props;

    kickedPlayer.isNotesDisplayed = 'kick';
    kickPlayer({appId, gameId, playerId});
  };

  onSendGiftModalClose = () => {
    this.setState({isSendGiftModalOpen: false});
  };

  onConfirmMedia = (params) => {
    const {confirmMedia, user: {profile: {id: userId}}, reInitializeVideo} = this.props;
    confirmMedia({...params, userId});
    reInitializeVideo();
    this.onSettingsClose();
  };

  onSettingsClose = () => {
    const {setMediaSettings} = this.props;

    setMediaSettings({visible: false});
    this.setState({displayGameSettings: false});
  };

  onMediaToggle = () => {
    this.setState({displayGameSettings: true});
  };

  onClearSelectedPlayer = () => {
    this.setState({playerId: null});
  };

  onExitFromGiftModal = () => {
    this.onClearSelectedPlayer();
    this.onSendGiftModalClose();
  };

  chatToggleHandler = () => {
    const {onChatToggle} = this.props;
    onChatToggle();
  };

  onReadyClick =() => {
    const {onPlayerReady, match: {params: {gameId}}} = this.props;
    this.setState({isReadyClicked: true, toastsDisplayed: true});
    onPlayerReady({appId, gameId});
  };

  onAnswerConfirm = (answerId) => {
    const {onPlayerAnswer, match: {params: {gameId}}} = this.props;
    if (answerId) {
      onPlayerAnswer({appId, gameId, answerId});
    }
  };

  getCurrentPlayer() {
    const {
      user: {profile: {id: userId}},
      game: {currentGame: {players}},
    } = this.props;

    return players.find(player => player.id === userId);
  }

  getCurrentUserId = () => {
    const {user: {profile: {id: userId}}} = this.props;

    return userId;
  };

  proceedleaveGame = () => {
    const {
      onPlayerLeave,
      match: {params: {gameId}},
    } = this.props;
    const currentPlayer = this.getCurrentPlayer();
    const {type, team} = currentPlayer || {};
    if (this.unblock) {
      this.unblock();
    }
    onPlayerLeave({
      appId, gameId, type, team,
    });
  };

  leaveGame = () => {
    toast.dismiss();
    confirm({
      theme: this.theme,
      message: gameLeaveWithRemoveMessage,
      proceed: this.proceedleaveGame,
    });
  }

  findUserById = (playerId) => {
    const {game: {currentGame: {players}}} = this.props;
    return players.find(player => player.id === playerId);
  }

  sendGift = (gift, playerId) => {
    const {onPlayerSendGift, match: {params: {gameId}}} = this.props;
    onPlayerSendGift({
      appId, gameId, gift, playerId,
    });
  };

  clearNotes = (playerId) => {
    const {onPlayerNotesClear} = this.props;
    onPlayerNotesClear(playerId);
  };

  messageForTeam = () => {
    const {game: {currentGame: {teams, winner}}} = this.props;
    const winnerTeam = teams[winner];

    return winnerTeam ? winFinishMessage(winnerTeam) : tieFinishMessage();
  };

  renderSettingsToggler = (theme) => {
    const currentPlayer = this.getCurrentPlayer();
    const isGameAudience = !currentPlayer;
    const toggleClasses = classNames(
      'media-toggle',
      {
        'media-toggle_disabled': isGameAudience,
      }
    );

    return (
      <div
        className={toggleClasses}
        onClick={this.onMediaToggle}
        style={{backgroundColor: theme.primaryColor}}
      >
        <Icon iconSize="22" glyph="sliders-h"/>
        <span>SETTINGS</span>
      </div>
    );
  };

  onVideoPause = () => this.setState({isVideoPlaying: false});

  onVideoPlay = () => this.setState({isVideoPlaying: true});

  renderQuestion = () => {
    const {
      game: {
        currentGame: {
          interstitial, question, questionIndex = 0, isAnswerChecking,
        },
      },
      user: {profile: {id: currentPlayerId}},
      onAnswerSelected,
    } = this.props;
    const {activePlayerId, isVideoPlaying} = this.state;
    const isCurrentPlayerTurn = currentPlayerId === activePlayerId;
    const isAudiencePlayer = !this.getCurrentPlayer();

    if (interstitial) {
      const {type} = interstitial;

      return type === 'image'
        ? (<QuestionImage {...interstitial} previewHidden fullwidth isInterstitial/>)
        : (
          <QuestionVideo
            isInterstitial
            {...interstitial}
            interstitial={interstitial}
            isVideoPlaying
            autoPlay
          />
        );
    }

    if (!question) {
      return null;
    }

    return (
      <Question
        question={question}
        isAudiencePlayer={isAudiencePlayer}
        questionIndex={questionIndex}
        onAnswerConfirm={this.onAnswerConfirm}
        onLeaveGame={this.leaveGame}
        isCurrentPlayerTurn={isCurrentPlayerTurn}
        onAnswerSelected={onAnswerSelected}
        isAnswerChecking={isAnswerChecking}
        onPlay={this.onVideoPlay}
        onPause={this.onVideoPause}
        isVideoPlaying={isVideoPlaying}
      />
    );
  };

  renderReadyMessage =() => (
    <div>
      <p>The game has not yet started.</p>
      <p>We are waiting for other players to click Ready.</p>
    </div>
  );

  renderReadyForm = () => {
    const {
      game: {currentGame: {players}}, gameConfig: {hasLeaderboard, hasTreasureChest},
    } = this.props;
    const {isReadyClicked, isTablet} = this.state;
    const currentPlayer = this.getCurrentPlayer();
    const isGameAudience = !currentPlayer;
    const lobbyLink = '/lobby';

    if (isGameAudience) {
      return (
        <div>
          <div>
            {this.renderReadyMessage()}
          </div>
          <div>
            <div className="row_gap_s">
              <Link color="#999999" to={lobbyLink} type="game__return" text="> Return to lobby"/>
            </div>
            <div>
              <Button handler={this.leaveGame} className="button_size_l" text="Leave"/>
            </div>
          </div>
        </div>
      );
    }

    const isAllPlayersReady = players
      .filter(player => !player.hasKicked)
      .every(player => player.isReady);
    const teams = countBy(players, player => player.team);
    const hasPlayersInBothTeams = Boolean(teams.left) && Boolean(teams.right);
    const {isReady} = currentPlayer;
    const isGameAdmin = currentPlayer.type === GAMEADMINTYPE;
    const disabled = (isGameAdmin && (!isAllPlayersReady || !hasPlayersInBothTeams))
      || (!isGameAdmin && isReady);
    const buttonTitle = (isGameAdmin ? 'Start' : 'Ready');
    const popoverTitle = isGameAdmin ? adminPopoverTitle : othersPopoverTitle;
    const popoverMessage = isGameAdmin ? adminPopoverMessage : othersPopoverMessage;
    const showButton = isGameAdmin || !disabled;
    const showMessage = !isGameAdmin && disabled;
    const hasDuringAndAfterBlock = hasLeaderboard || hasTreasureChest;

    return (
      <ThemeContext.Consumer>
        {theme => (
          <Manager>
            <div className="row row_center text-center">
              {showButton && (
                <div>
                  <Target>
                    <Button
                      tabIndex={-1}
                      className="button button_primary button_size_lg button_blinking"
                      disabled={disabled || isReadyClicked}
                      handler={this.onReadyClick}
                      text={buttonTitle}
                    />
                    <div className="game__rules">
                      <h2>GAME RULES</h2>
                      <ul className="game__rules__list">
                        <li>
                          {`Click ${buttonTitle.toUpperCase()}`}
                        </li>
                        <li>HAND indicates when it is your turn</li>
                        <li>
                          {'Click on the GIFT ICON '}
                          <span
                            className="icon-gift team-player__send-gift team-player__send-gift_small"
                            style={{
                              backgroundColor: `${color(theme.primaryColor).darken(0.2).hexString()}`,
                            }}
                          />
                          {' to send a player a GIFT'}
                        </li>
                        <li>Players can vote to KICK out an unruly player</li>
                        {hasDuringAndAfterBlock
                        && (
                          <li>
                            During and after the game…
                            <ul>
                              {hasLeaderboard
                            && (
                              <li>
                              Check the LEADERBOARD on the
                                {isTablet
                                  ? (
                                    <span>
                                      <span> Hamburger menu </span>
                                      <span
                                        className="icon icon-bars team-player__send-gift team-player__send-gift_small"
                                        style={{
                                          backgroundColor: `${color(theme.primaryColor).darken(0.2).hexString()}`,
                                        }}
                                      />
                                    </span>)
                                  : (<span> Menu above </span>)
                                }
                              </li>
                            )}
                              {hasTreasureChest
                            && (
                              <li>
                              Check the TREASURE CHEST on the
                                {isTablet
                                  ? (
                                    <span>
                                      <span> Hamburger menu </span>
                                      <span
                                        className="icon icon-bars team-player__send-gift team-player__send-gift_small"
                                        style={{
                                          backgroundColor: `${color(theme.primaryColor).darken(0.2).hexString()}`,
                                        }}
                                      />&nbsp;
                                    </span>)
                                  : (<span> Menu above </span>)
                                }
                              to Redeem Your YakGold.
                              </li>
                            )}
                              <li>
                                Check YAKSILVER COINS
                                <img className="coins-popper__icon" src={silverCoinImage} height="20" alt=""/>
                                next to your profile picture
                              </li>
                            </ul>
                          </li>
                        )}
                        <li>Having trouble with your A/V… click SETTINGS
                          {
                            isTablet && (
                              <span>
                                <span> Hamburger menu </span>
                                <span
                                  className="icon icon-bars team-player__send-gift team-player__send-gift_small"
                                  style={{
                                    backgroundColor: `${color(theme.primaryColor).darken(0.2).hexString()}`,
                                  }}
                                />
                              </span>
                            )
                          }
                        </li>
                      </ul>
                    </div>
                  </Target>
                  <Media query={{minWidth: 700}}>
                    <Popover title={popoverTitle}>
                      {popoverMessage}
                    </Popover>
                  </Media>
                </div>
              )
              }
              {showMessage && this.renderReadyMessage()}
            </div>
            <div>
              <div className="row_gap_s">
                <Link color="#999999" to={lobbyLink} type="game__return" text="> Return to lobby"/>
              </div>
              <div>
                <Link
                  onClick={this.leaveGame}
                  theme="primary"
                  size="l"
                  type="button"
                  text="Leave"
                />
              </div>
            </div>
          </Manager>
        )}
      </ThemeContext.Consumer>
    );
  };

  onCoinPacksModalOpen = () => this.setState({isCoinPacksModalOpen: true});

  onCoinPacksModalClose = () => this.setState({isCoinPacksModalOpen: false});

  renderFinish = () => {
    const message = this.messageForTeam();
    const lobbyLink = '/lobby';

    if (this.unblock) {
      this.unblock();
    }

    return (
      <div className="row row_center text-center">
        <div>
          <h2>{message}</h2>
          <h2>The Game is Over!</h2>
        </div>
        <div>
          <div className="row_gap_s">
            <ThemeContext.Consumer>
              {({primaryColor}) => (
                <Link
                  color="#ffffff"
                  bgColor={primaryColor}
                  to={lobbyLink}
                  type="button button_primary button_size_l"
                  text="Leave Game"
                />
              )}
            </ThemeContext.Consumer>
          </div>
        </div>
      </div>
    );
  };

  onSidebarToggle = () => {
    const {setSidebarStatus} = this.props;

    setSidebarStatus({status: true});
  }

  scrollHandle = (e) => {
    const {phase} = this.state;
    if (phase === GAME_PHASE_PLAYING) {
      return;
    }
    const contentWrap = e.target.closest('.game__content-wrap').scrollTop;
    if (contentWrap > 0) {
      this.setState({arePlayersExpanded: false});
    } else {
      this.setState({arePlayersExpanded: true});
    }
  }

  render() {
    const {
      application: {ableToSendGiftsForFree},
      game: {
        currentGame: {
          interstitial,
          players = [],
          round = {},
          // Defensive defaults: `teams` is a client-side construct (initial state
          // + Ably point updates), so a transitional/partial currentGame during
          // navigation can momentarily lack it. An unguarded destructure here
          // throws in render and the top-level boundary blanks the whole app.
          teams: {
            left: {name: leftTeamName, points: leftTeamPoints} = {},
            right: {name: rightTeamName, points: rightTeamPoints} = {},
          } = {},
          kickRequest,
        },
      },
      gifts,
      onPlayerKickCancel,
      user: {
        // `coins` is absent from the user reducer's initial state (it's only set
        // on profile fetch / coins events), so guard the nested destructure —
        // otherwise rendering the game before coins are present throws "cannot
        // destructure 'silver' of undefined" and the whole view crashes into the
        // error boundary ("Something went wrong").
        audioSource, videoSource, coins: {silver: silverCoins} = {}, mediaSettings,
      },
      panel: {isChatPanelOpened},
      updateUserCoins,
    } = this.props;
    const hasGifts = Boolean(gifts.length);
    const {title = ''} = round;
    const {
      isCoinPacksModalOpen,
      isSendGiftModalOpen,
      playerId,
      phase,
      activePlayerId,
      displayGameSettings,
      isVideoPlaying,
      arePlayersExpanded,
    } = this.state;

    const currentPlayer = players.filter(player => player.id === activePlayerId);
    const leftTeam = players.filter(player => player.team === 'left');
    const rightTeam = players.filter(player => player.team === 'right');

    const dispatcher = {
      [GAME_PHASE_WAITING_FOR_PLAYERS]: this.renderReadyForm,
      [GAME_PHASE_PLAYING]: this.renderQuestion,
      [GAME_PHASE_FINISHED]: this.renderFinish,
    };
    const renderFn = dispatcher[phase] || (() => {});
    const isGameFinished = phase === GAME_PHASE_FINISHED;

    const gameClasses = classNames(
      'game',
      {
        game_open: phase === GAME_PHASE_WAITING_FOR_PLAYERS,
        game_play: phase === GAME_PHASE_PLAYING,
        game_finish: isGameFinished,
      }
    );
    const roundTitle = isGameFinished ? '' : unescape(title);
    const nonFinishedPlayerId = isGameFinished ? null : activePlayerId;
    const isAudiencePlayer = !this.getCurrentPlayer();
    const showKickRequest = kickRequest && !isAudiencePlayer;
    const isInterstitialPlaying = Boolean(interstitial) && interstitial.type === 'video';
    const hasSilverCoins = ableToSendGiftsForFree || Boolean(silverCoins);
    const modalTitle = hasSilverCoins ? 'Choose a Gift' : 'Buy More YakSilver';
    const needToExpand = arePlayersExpanded && phase !== GAME_PHASE_PLAYING;

    return (
      <ThemeContext.Consumer>
        {(theme) => {
          const panelStyles = {
            backgroundColor: theme.secondaryColor,
          };
          const paymentPacks = Object.keys(theme.paymentPacks).map(key => theme.paymentPacks[key]);

          this.theme = theme;

          return (
            <div className="game-panel">
              <Media query={{maxWidth: 710, orientation: 'landscape'}}>
                {!isChatPanelOpened &&
                  <div className="orientation-warning">
                    <div className="orientation-warning__text">
                      Please rotate your device
                    </div>
                  </div>
                }
              </Media>
              <div className="game-header row row_space-between row_vcenter" style={panelStyles}>
                <div className="cell cell_auto">
                  <TeamPoints name={leftTeamName} points={leftTeamPoints}/>
                </div>
                <Media queries={{tablet: {maxWidth: 880}}}>
                  {
                    matches =>
                      (
                        matches.tablet ? (
                          <div className="flex">
                            <div className="cell cell_auto">
                              <TeamPoints name={rightTeamName} points={rightTeamPoints}/>
                            </div>
                            <Button
                              size="m"
                              iconSize={20}
                              icon="bars"
                              className="top-menu-btn"
                              handler={this.onSidebarToggle}
                            />
                          </div>
                        ) : (
                          <Fragment>
                            <div className="cell">
                              <div className="round-title">{roundTitle}</div>
                            </div>
                            <div className="cell cell_auto">
                              <TeamPoints name={rightTeamName} points={rightTeamPoints}/>
                            </div>
                          </Fragment>
                        )
                      )
                  }
                </Media>
              </div>
              <div className="game-panel__content">
                <div className={gameClasses}>
                  <Media queries={{phone: {maxWidth: 700, orientation: 'portrait'}}}>
                    {
                      matches =>
                        (
                          matches.phone ? (
                            <Fragment>
                              <div
                                className="game__content-wrap"
                                onScroll={this.scrollHandle}
                              >
                                {renderFn({theme})}
                              </div>
                              <div className={classNames('game__teams', {game__teams_expanded: needToExpand})}>
                                <div className="game__team">
                                  <Team
                                    {...this.props}
                                    players={leftTeam}
                                    giftsAlign="right"
                                    activePlayerId={nonFinishedPlayerId}
                                    playerId={playerId}
                                    onVoteForKickClicked={this.onVoteForKickClicked}
                                    onSendGiftClicked={this.onSendGiftClicked}
                                    onSelectPlayer={this.onSelectPlayer}
                                    clearNotes={this.clearNotes}
                                    hasGifts={hasGifts}
                                    isAudiencePlayer={isAudiencePlayer}
                                    isGameFinished={isGameFinished}
                                    isInterstitialPlaying={isInterstitialPlaying}
                                    isVideoPlaying={isVideoPlaying}
                                  />
                                </div>
                                <div className="game__team">
                                  <Team
                                    {...this.props}
                                    players={rightTeam}
                                    giftsAlign="left"
                                    activePlayerId={nonFinishedPlayerId}
                                    playerId={playerId}
                                    onVoteForKickClicked={this.onVoteForKickClicked}
                                    onSendGiftClicked={this.onSendGiftClicked}
                                    onSelectPlayer={this.onSelectPlayer}
                                    clearNotes={this.clearNotes}
                                    hasGifts={hasGifts}
                                    isAudiencePlayer={isAudiencePlayer}
                                    isGameFinished={isGameFinished}
                                    isInterstitialPlaying={isInterstitialPlaying}
                                    isVideoPlaying={isVideoPlaying}
                                  />
                                </div>
                              </div>
                              <InteractiveNavigation
                                playerId={playerId}
                                currentPlayerId={currentPlayer.id}
                                isAudiencePlayer={isAudiencePlayer}
                                getCurrentUserId={this.getCurrentUserId}
                                startGameHandler={this.onReadyClick}
                                chatToggleHandler={this.chatToggleHandler}
                                onVoteForKickClicked={this.onVoteForKickClicked}
                                onClearSelectedPlayer={this.onClearSelectedPlayer}
                                onSendGiftClicked={this.onSendGiftClicked}
                              />
                            </Fragment>
                          ) : (
                            <Fragment>
                              <div className="game__team">
                                <Team
                                  {...this.props}
                                  players={leftTeam}
                                  giftsAlign="right"
                                  activePlayerId={nonFinishedPlayerId}
                                  playerId={playerId}
                                  onVoteForKickClicked={this.onVoteForKickClicked}
                                  onSendGiftClicked={this.onSendGiftClicked}
                                  onSelectPlayer={() => {}}
                                  clearNotes={this.clearNotes}
                                  hasGifts={hasGifts}
                                  isAudiencePlayer={isAudiencePlayer}
                                  isGameFinished={isGameFinished}
                                  isInterstitialPlaying={isInterstitialPlaying}
                                  isVideoPlaying={isVideoPlaying}
                                />
                              </div>
                              <div className="game__content-wrap">
                                {renderFn({theme})}
                              </div>
                              <div className="game__team">
                                <Team
                                  {...this.props}
                                  players={rightTeam}
                                  giftsAlign="left"
                                  activePlayerId={nonFinishedPlayerId}
                                  playerId={playerId}
                                  onVoteForKickClicked={this.onVoteForKickClicked}
                                  onSendGiftClicked={this.onSendGiftClicked}
                                  onSelectPlayer={() => {}}
                                  clearNotes={this.clearNotes}
                                  hasGifts={hasGifts}
                                  isAudiencePlayer={isAudiencePlayer}
                                  isGameFinished={isGameFinished}
                                  isInterstitialPlaying={isInterstitialPlaying}
                                  isVideoPlaying={isVideoPlaying}
                                />
                              </div>
                            </Fragment>
                          )
                        )
                    }
                  </Media>
                </div>
              </div>
              {showKickRequest && (
                <Modal
                  title="Cast a vote to kick a player out!"
                  isOpen
                  onClose={onPlayerKickCancel}
                  contentStyles="modal__content_padding_s"
                  size="fixed"
                >
                  <KickedPlayerModal
                    kickRequest={kickRequest}
                    onModalClose={onPlayerKickCancel}
                    onPlayerKicked={this.onPlayerKicked}
                  />
                </Modal>
              )}
              {hasGifts
                && (
                  <Modal
                    title={modalTitle}
                    isOpen={isSendGiftModalOpen}
                    onClose={this.onSendGiftModalClose}
                    contentStyles="modal__content_padding_s"
                    textAlign="center"
                    size="m"
                  >
                    {!hasSilverCoins && (
                      <Fragment>
                        <div className="alert alert-warning">You don&rsquo;t have enough YakSilver.</div>
                        <Button
                          className="mh-auto"
                          text="Buy More YakSilver"
                          handler={this.onCoinPacksModalOpen}
                        />
                        <CoinsPackModal
                          isModalOpened={isCoinPacksModalOpen}
                          onModalClose={this.onCoinPacksModalClose}
                          paymentPacks={paymentPacks}
                          updateUserCoins={updateUserCoins}
                        />
                      </Fragment>
                    )}
                    { hasSilverCoins && (
                      <Fragment>
                        {!ableToSendGiftsForFree && (
                          <div className="alert alert-warning">
                            It costs 1 YakSilver to send a gift
                          </div>
                        )}
                        <SendGiftModal
                          gifts={gifts}
                          playerId={playerId}
                          onModalClose={this.onSendGiftModalClose}
                          sendGift={this.sendGift}
                        />
                      </Fragment>
                    )}
                    { (window.innerWidth < 700) && (
                      <InteractiveNavigation
                        modal="gift"
                        playerId={playerId}
                        currentPlayerId={currentPlayer.id}
                        isAudiencePlayer={isAudiencePlayer}
                        getCurrentUserId={this.getCurrentUserId}
                        startGameHandler={this.onReadyClick}
                        chatToggleHandler={this.chatToggleHandler}
                        onVoteForKickClicked={this.onVoteForKickClicked}
                        onClearSelectedPlayer={this.onExitFromGiftModal}
                        onSendGiftClicked={this.onExitFromGiftModal}
                      />
                    )}
                  </Modal>
                )
              }
              <ChangeMediaConfigurationModal
                isModalOpened={displayGameSettings || mediaSettings}
                onModalClose={this.onSettingsClose}
                onConfirmMedia={this.onConfirmMedia}
                defaultAudioSource={audioSource || NOMEDIASOURCE}
                defaultVideoSource={videoSource || NOMEDIASOURCE}
              />
              {this.renderSettingsToggler(theme)}
            </div>
          );
        }}
      </ThemeContext.Consumer>
    );
  }
}
